import React, { useRef, useState } from "react";
import { Modal, Typography, Button, Divider, message, Row, Col, Space } from "antd";
import { PaperClipOutlined, EyeOutlined, SendOutlined } from "@ant-design/icons";
import RightSidePreviewModal from "./RightSidePreviewModal";

const { Text } = Typography;

const BRAND = "rgb(0, 76, 143)";
const BRAND_LIGHT = "#e8f0fa";
const BRAND_TEXT = "#004c8f";

interface Attachment {
  filename: string;
  url: string;
}

interface EmailData {
  to?: string;
  from?: string;
  EmailSubject?: string;
  EmailBody?: string;
  HealthCareID?: number;
  SchemeId?: number;
  attachments?: Attachment[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  emailData: any;
  handleSubmitEmail: any;
  isSubmittingEmail: boolean;
}

const EmailPreviewModal: React.FC<Props> = ({
  open,
  onClose,
  emailData,
  handleSubmitEmail,
  isSubmittingEmail,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const emailDetails = emailData;
  const attachments: Attachment[] = emailData?.AttachmentUrls || [];
  const [previewDocument, setPreviewDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string>("");

  const format = (cmd: string) => document.execCommand(cmd);

  const handleSave = async () => {
    if (!editorRef.current) return;

    try {
      const payload: EmailData = {
        ...emailDetails,
        EmailBody: editorRef.current.innerHTML,
        attachments,
      };

      handleSubmitEmail(payload);
    } catch {
      message.error("Failed to send email");
    }
  };

  const metaField = (label: string, value: string) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Text
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#9ca3af",
          fontWeight: 500,
        }}
      >
        {label}
      </Text>
      <Text style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>
        {value || "—"}
      </Text>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="100vw"
        wrapClassName="patient-list-table-modal"
        style={{ top: 2, left: 4, padding: 0, maxWidth: "100vw", height: "140vh" }}
        bodyStyle={{
          padding: 0,
          background: "#f3f4f6",
          height: "calc(140vh)",
          overflow: "auto",
        }}
      >
        {/* ── Header ── */}
        <div style={{ padding: "20px 24px 0", background: "#fff" }}>
          {/* Title row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: BRAND_LIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SendOutlined style={{ color: BRAND_TEXT, fontSize: 15 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#111827", lineHeight: 1.3 }}>
                Email Preview
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }}>
                Review and edit before sending
              </div>
            </div>
          </div>

          {/* Meta fields in a bordered container */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              overflow: "hidden",
              marginBottom: 16,
            }}
          >
            {/* To / From row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
              <div
                style={{
                  padding: "12px 16px",
                  borderRight: "1px solid #e5e7eb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                {metaField("To", emailDetails?.to)}
              </div>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
                {metaField("From", emailDetails?.from)}
              </div>
            </div>
            {/* Subject row */}
            <div style={{ padding: "12px 16px" }}>
              {metaField("Subject", emailDetails?.EmailSubject || "No subject provided")}
            </div>
          </div>

          {/* Attachments */}
          {attachments?.length > 0 && (
            <div
              style={{
                marginBottom: 20,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {/* Section header */}
              <div
                style={{
                  padding: "9px 14px",
                  background: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <PaperClipOutlined style={{ color: BRAND_TEXT, fontSize: 13 }} />
                <Text style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                  Attachments
                </Text>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 11,
                    background: BRAND_LIGHT,
                    color: BRAND_TEXT,
                    borderRadius: 99,
                    padding: "1px 8px",
                    fontWeight: 600,
                  }}
                >
                  {attachments.length} file{attachments.length > 1 ? "s" : ""}
                </span>
              </div>

              {/* Attachment chips */}
              <div style={{ padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                {attachments.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 12px",
                      borderRadius: 6,
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      minWidth: 160,
                      maxWidth: 260,
                    }}
                  >
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 5,
                        background: BRAND_LIGHT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PaperClipOutlined style={{ color: BRAND_TEXT, fontSize: 11 }} />
                    </div>
                    <Text
                      ellipsis={{ tooltip: item.filename }}
                      style={{ flex: 1, fontSize: 12, color: "#374151", fontWeight: 500 }}
                    >
                      {item.filename}
                    </Text>
                    <EyeOutlined
                      style={{ color: "#9ca3af", fontSize: 13, cursor: "pointer", flexShrink: 0 }}
                      onClick={() => {
                        setPreviewDocument(true);
                        setDocumentUrl(item?.url);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <Divider style={{ margin: 0 }} />

        {/* ── Editor ── */}
        <div
          style={{
            margin: 16,
            background: "#fff",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          {/* Toolbar */}
          <div
            style={{
              padding: "8px 12px",
              borderBottom: "1px solid #eee",
              background: "#f9fafb",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {[
              { label: <b>B</b>, cmd: "bold" },
              { label: <i>I</i>, cmd: "italic" },
              { label: <u>U</u>, cmd: "underline" },
            ].map(({ label, cmd }) => (
              <button
                key={cmd}
                onClick={() => format(cmd)}
                style={{
                  padding: "3px 10px",
                  fontSize: 13,
                  border: "1px solid #e5e7eb",
                  borderRadius: 5,
                  background: "#fff",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                {label}
              </button>
            ))}

            <Divider type="vertical" style={{ margin: "0 4px" }} />

            <button
              onClick={() => format("insertUnorderedList")}
              style={{
                padding: "3px 10px",
                fontSize: 13,
                border: "1px solid #e5e7eb",
                borderRadius: 5,
                background: "#fff",
                cursor: "pointer",
                color: "#374151",
              }}
            >
              • List
            </button>
            <button
              onClick={() => format("insertOrderedList")}
              style={{
                padding: "3px 10px",
                fontSize: 13,
                border: "1px solid #e5e7eb",
                borderRadius: 5,
                background: "#fff",
                cursor: "pointer",
                color: "#374151",
              }}
            >
              1. List
            </button>
          </div>

          {/* Editable body */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            dangerouslySetInnerHTML={{ __html: emailData?.EmailBody }}
            style={{
              padding: "16px 20px",
              minHeight: 300,
              maxHeight: 340,
              overflowY: "auto",
              fontSize: 14,
              lineHeight: 1.75,
              outline: "none",
              color: "#111827",
            }}
          />
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "12px 20px",
            background: "#fff",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={onClose}
            style={{ borderRadius: 6, fontSize: 13, height: 36, color: "#374151" }}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            loading={isSubmittingEmail}
            icon={<SendOutlined />}
            onClick={handleSave}
            style={{
              backgroundColor: BRAND,
              borderColor: BRAND,
              color: "#fff",
              borderRadius: 6,
              fontSize: 13,
              height: 36,
              fontWeight: 500,
              boxShadow: "0 1px 4px rgba(0,76,143,0.3)",
            }}
          >
            Send Email
          </Button>
        </div>
      </Modal>

      <RightSidePreviewModal
        key={documentUrl}
        documentUrl={documentUrl}
        handleCancel={() => setPreviewDocument(false)}
        open={previewDocument}
      />
    </>
  );
};

export default EmailPreviewModal;